import config from "_src/config";
import {blendFactor, drawMode} from "_src/engine/constants";
import objFactory from "_src/engine/factories/object";
import minmaxUtils from "_src/engine/utility/minmax";

import {mat4, vec3} from "_src/libs/gl-matrix";

import ObjectWrapper from "_src/object_wrapper";
import {objectType} from "_src/constants";
import DefaultShader from "_src/shaders/default/default_shader";
import appState from "_src/app_state";

const PREVIEW_OPACITY = config.postBlending ? 0.3 : 0.1;
const objectToPreview = new WeakMap();

export default {

  createPreview(object, transform) {

    const previewShader = new DefaultShader({
      transparent: true,
      backFaceCulling: false,
      sourceBlendFactor: blendFactor.ONE,
      destBlendFactor: blendFactor.ONE,
      lighting: false,
      drawDepth: false,
      writeObjectID: false,
      backFaceMode: appState.BM_FRONTFACE,
    });
    previewShader.uniforms["u_text_proj_matrix"].value = mat4.clone(object.model.shader.uniforms["u_text_proj_matrix"].value);
    previewShader.setObjectSize(object.model.shader.uniforms["u_object_size"].value);

    const previewModel = objFactory.createDrawable(previewShader);
    previewModel.setOpacity(PREVIEW_OPACITY);
    previewModel.textures = object.model.textures;
    for (const chunk of object.model.chunks) {
      previewModel.addChunk(chunk)
    }

    previewModel.modelMatrix = mat4.multiply(previewModel.modelMatrix, transform, object.model.modelMatrix);
    previewModel.setMatrix(previewModel.modelMatrix);

    const previewObject = new ObjectWrapper({
      model   : previewModel,
    });

    minmaxUtils.copy(previewObject.getWorldMinmax(), object.getWorldMinmax());
    minmaxUtils.recomputedFromRotated(previewObject.getWorldMinmax(), object.getWorldMinmax(), transform);

    objectToPreview.set(object, previewObject);
    this.updatePreview(object);

    return previewObject;
  },

  updatePreview(object) {
    const previewObject = objectToPreview.get(object);
    if (!previewObject || !object.loaded) {
      return;
    }

    const previewModel = previewObject.model;

    const previewChildren = previewModel.getChildren();
    const objectChildren = object.model.getChildren();
    if (previewChildren.length !== objectChildren.length) {
      while (previewChildren.length > 0) {
        previewModel.remove(previewChildren[0]);
      }

      for (const child of object.model.getChildren()) {
        let previewChild = objFactory.createDrawable(previewModel.shader);
        previewChild.setOpacity(PREVIEW_OPACITY);
        previewChild.textures = child.textures;

        previewChild.copyScale(child.scale);
        previewChild.copyTranslation(child.trans);
        previewChild.copyRotation(child.quat);
        previewChild.updateMatrix();

        for (const chunk of child.chunks) {
          previewChild.addChunk(chunk);
        }

        previewModel.add(previewChild);
      }

      //this is a hack to propagate parent transformation to children
      previewModel.setMatrix(previewModel.modelMatrix);
    }

    vec3.copy(previewModel.properties.diffuseColor, object.model.properties.diffuseColor);

    const visibility = object.getVisibility() && !object.getLockState();
    previewModel.setVisibility(visibility);
    for (let i=0; i<previewChildren.length; i++) {
      vec3.copy(previewChildren[i].properties.diffuseColor, objectChildren[i].properties.diffuseColor);
      previewChildren[i].setVisibility(visibility && !objectChildren[i].checkIsHidden());
    }

    const colorMode = object.resolveColorMode(appState.getStateValue("colorMode") === appState.CM_TEXTURE);
    previewModel.setColorMode(colorMode);
    if (object.type === objectType.SURFELS) {
      previewModel.shader.setDirective("SURFELS_MODE", true);
      previewModel.shader.setSurfelsFiltering(appState.SM_NONE);
      previewModel.setDrawMode(drawMode.POINTS);
      previewModel.shader.setDrawMode(drawMode.POINTS);
    }
  }

}
